import { ArrowReplayIcon, CircleFilledIcon, CircleIcon } from '@approvalmax/ui';
import { Box, Flex, StatusLabel, Text } from '@approvalmax/ui/src/components';
import { dateTimeHelpers } from '@approvalmax/utils';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { selectors } from 'modules/common';
import { getActiveRequest } from 'pages/requestList/selectors/pageSelectors';
import { memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { getVersionTypeDescription } from './VersionHistoryItem.helpers';
import { messages } from './VersionHistoryItem.messages';
import { Content } from './VersionHistoryItem.styles';
import { VersionHistoryItemProps } from './VersionHistoryItem.types';

export const VersionHistoryItem = memo<VersionHistoryItemProps>((props) => {
    const { active, checked, isFirst, isLast, requestVersion, isDextRequest, onSelectVersion } = props;

    const userName = useMemo(() => {
        if (!requestVersion.actor) {
            return '';
        }

        return selectors.user.getDisplayName(
            requestVersion.actor.firstName,
            requestVersion.actor.lastName,
            requestVersion.actor.userEmail || ''
        );
    }, [requestVersion.actor]);

    const request = useSelector(getActiveRequest);
    const versionTypeDescription = getVersionTypeDescription({
        version: requestVersion.versionType,
        integrationType: request.integrationType,
        isDextRequest,
        userName,
    });

    const handleClick = useCallback(() => {
        onSelectVersion(requestVersion);
    }, [onSelectVersion, requestVersion]);

    const dotIcon = useMemo(() => {
        switch (true) {
            case active:
                return <CircleFilledIcon size={8} color={checked ? 'green100' : 'green70'} />;

            case checked:
                return <CircleFilledIcon size={8} color='midnight60' />;

            default:
                return <CircleIcon size={8} color='midnight60' />;
        }
    }, [active, checked]);

    const commentTextContent = useMemo(
        () => requestVersion.comment?.text && parse(DOMPurify.sanitize(requestVersion.comment.text)),
        [requestVersion.comment]
    );

    return (
        <Flex spacing='8' wrap={false}>
            <Flex direction='column' spacing='0' width={8} alignItems='center'>
                <Flex height={14} justifyContent='center' width={1}>
                    {!isFirst && <Box color='midnight60' width={1} height='100%' />}
                </Flex>

                {dotIcon}

                <Flex grow={1} width={1}>
                    {!isLast && <Box color='midnight60' width={1} height='100%' />}
                </Flex>
            </Flex>

            <Flex grow={1}>
                <Content
                    onClick={handleClick}
                    spacing='8 12'
                    width='fill-available'
                    radius='xsmall'
                    $active={active}
                    $checked={checked}
                >
                    <Flex spacing='8' wrap={false} justifyContent='space-between' alignItems='center'>
                        <div>
                            <Text font='body' fontSize='small' fontWeight='medium' color='black100' spacing='0 0 3'>
                                {messages.versionTitle({ version: requestVersion.localVersionNumber })}

                                {' - '}

                                <Text font='body' as='span' color='midnight70'>
                                    {versionTypeDescription}
                                </Text>
                            </Text>

                            <Text font='body' fontSize='small' color={active || checked ? 'midnight100' : 'midnight70'}>
                                {dateTimeHelpers.formatDateTime(requestVersion.createdAt, 'DateAndTime')}
                            </Text>

                            {requestVersion.comment && (
                                <Flex spacing='2' wrap={false} alignItems='end'>
                                    <ArrowReplayIcon color='midnight70' size={16} />

                                    <Text font='label' fontSize='small' color='midnight70'>
                                        {commentTextContent}
                                    </Text>
                                </Flex>
                            )}
                        </div>

                        {active && <StatusLabel size='small'>{messages.active}</StatusLabel>}
                    </Flex>
                </Content>
            </Flex>
        </Flex>
    );
});

VersionHistoryItem.displayName = 'VersionHistoryItem';
